import { size } from "lodash";
import {
  BetaFeature as BetaFeatureId,
  DismissOrigin
} from "@tvg/sh-lib-beta-program/types";
import {
  getUserAllowedFeatures,
  isOptedInBetaFeature
} from "@tvg/sh-lib-beta-program/utils/handlers";
import { gtmViewExitModal } from "@tvg/sh-lib-beta-program/gtm";
import { useTheme } from "@tvg/design-system/src";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ConfirmationModal, FeaturesPreview } from "../../components";
import {
  getBetaFeaturesPreviewModal,
  getBetaWhitelist,
  getUser
} from "../../redux/selectors";
import { BetaFeatures, Props } from "./types";
import {
  closeModalAndDismissFeature,
  executeGtmOnOpen,
  fetchStoryblokFeatures,
  generateModalConfig,
  getBetaFeatureList,
  getCTA,
  getEnrollmentDate,
  getFirstBetaFeature,
  onCancelExit,
  onConfirmExit,
  openModalOnMount,
  renderListItems
} from "./utils";
import { useBetaFeatureInfo } from "./hooks";

const BetaFeaturesModal = ({ redirectUrl }: Props) => {
  // REACT HOOKS
  const [isExitingConfirmation, setIsExitingConfirmation] = useState(false);
  const [isViewingMore, setViewingMore] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState<BetaFeatureId>(
    BetaFeatureId.PROGRAM_PAGE
  );
  const [betaFeatures, setBetaFeatures] = useState<BetaFeatures>({});
  const dispatch = useDispatch();
  const theme = useTheme();

  // SELECTORS
  const { accountNumber, isLogged } = useSelector(getUser);
  const whitelist = useSelector(getBetaWhitelist);
  const { isOpen } = useSelector(getBetaFeaturesPreviewModal);

  // VARIABLES
  const isUserEnrolled = isOptedInBetaFeature(accountNumber, selectedFeature);
  const features = getUserAllowedFeatures(accountNumber, whitelist);
  const noFeaturesAvailable = features.length === 0;
  const timestamp = isUserEnrolled
    ? getEnrollmentDate(accountNumber, selectedFeature)
    : "";

  const list = noFeaturesAvailable
    ? []
    : renderListItems(
        getBetaFeatureList(betaFeatures),
        selectedFeature,
        setSelectedFeature,
        theme
      );

  const { preview, confirmation } = useBetaFeatureInfo({
    selectedFeature,
    isUserEnrolled,
    betaFeatures,
    noFeaturesAvailable
  });

  const shouldOpen = isOpen && isLogged;

  useEffect(() => {
    fetchStoryblokFeatures(features).then((featuresFormatted) => {
      setBetaFeatures(featuresFormatted);
      setSelectedFeature(getFirstBetaFeature(featuresFormatted));
    });
  }, [JSON.stringify(features)]);

  useEffect(
    openModalOnMount(accountNumber, whitelist, !!size(betaFeatures), dispatch),
    [JSON.stringify(whitelist), betaFeatures]
  );

  useEffect(executeGtmOnOpen(isOpen, selectedFeature), [
    isOpen,
    selectedFeature
  ]);

  useEffect(() => {
    if (isExitingConfirmation) gtmViewExitModal();
  }, [isExitingConfirmation]);

  const modalConfig = generateModalConfig(isUserEnrolled);

  return (
    <>
      <FeaturesPreview
        isOpen={shouldOpen}
        toggleViewMore={() => setViewingMore((prevState) => !prevState)}
        isViewingMore={isViewingMore}
        noFeaturesAvailable={noFeaturesAvailable}
        list={list}
        description={preview?.description}
        details={preview?.details}
        onPress={getCTA(
          isUserEnrolled,
          selectedFeature,
          accountNumber,
          whitelist,
          setIsExitingConfirmation,
          selectedFeature === BetaFeatureId.PROGRAM_PAGE
            ? redirectUrl
            : undefined,
          dispatch,
          modalConfig.ctaText
        )}
        config={modalConfig}
        onClose={closeModalAndDismissFeature(
          dispatch,
          accountNumber,
          whitelist,
          DismissOrigin.CROSS,
          selectedFeature
        )}
        onOverlayClose={closeModalAndDismissFeature(
          dispatch,
          accountNumber,
          whitelist,
          DismissOrigin.OVERLAY,
          selectedFeature
        )}
        timestamp={timestamp}
        theme={theme}
      />
      <ConfirmationModal
        title={confirmation?.title}
        subtitle={confirmation?.subtitle}
        description={confirmation?.description}
        isOpen={isExitingConfirmation}
        onClose={onCancelExit(
          accountNumber,
          whitelist,
          selectedFeature,
          setIsExitingConfirmation
        )}
        onConfirm={onConfirmExit(
          accountNumber,
          whitelist,
          selectedFeature,
          setIsExitingConfirmation
        )}
      />
    </>
  );
};

export default React.memo(BetaFeaturesModal);
